import { useCallback, useEffect, useRef } from "react";
import { IdPTokens, LoginWithIdPOptions } from "./types";

/**
 * The page that starts the silent login. The callback returns the user there, and the SendGrid
 * session is kept for both the silent login and its interactive fallback.
 */
export type HeldTokensOrSilentLoginOptions =
  | { fromUnifiedAccountLink: true; fromUnifiedAccountSwitching?: never }
  | { fromUnifiedAccountSwitching: true; fromUnifiedAccountLink?: never };

export type HeldTokensOrSilentLoginResult =
  /** Tokens were already held. */
  | { status: "held"; tokens: IdPTokens }
  /** None were held, and a silent login is sending the browser to the IdP. */
  | { status: "redirecting" }
  /** None were held, and no silent login was started. */
  | { status: "unavailable" };

/**
 * Returns the tokens already held, or starts a silent (`prompt=none`) login when there are none.
 * At most one silent login starts per page load, and none when `mayStartSilentLogin` returns false.
 */
export function useHeldTokensOrSilentLogin(
  getHeldTokens: () => Promise<IdPTokens | undefined>,
  login: (opts: LoginWithIdPOptions) => Promise<void>,
  mayStartSilentLogin: () => boolean
): (opts: HeldTokensOrSilentLoginOptions) => Promise<HeldTokensOrSilentLoginResult> {
  // The silent login started on this page load, so a second caller gets the same outcome.
  const silentLogin = useRef<Promise<HeldTokensOrSilentLoginResult>>();

  useEffect(() => {
    // Restored from the back/forward cache after a silent login left the page: start over.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && silentLogin.current) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return useCallback(
    async ({ fromUnifiedAccountLink, fromUnifiedAccountSwitching }) => {
      let tokens: IdPTokens | undefined;
      try {
        tokens = await getHeldTokens();
      } catch (error) {
        // A failed read is treated as no tokens held: a silent login gets new ones.
        console.error("Reading the held IdP tokens failed", error);
      }
      if (tokens) {
        return { status: "held", tokens };
      }
      if (silentLogin.current) {
        return silentLogin.current;
      }
      if (!mayStartSilentLogin()) {
        return { status: "unavailable" };
      }
      const attempt: Promise<HeldTokensOrSilentLoginResult> = Promise.resolve()
        .then(() => login({ fromUnifiedAccountLink, fromUnifiedAccountSwitching, silent: true }))
        .then(
          () => ({ status: "redirecting" }),
          (error) => {
            // Let a later call try again.
            if (silentLogin.current === attempt) {
              silentLogin.current = undefined;
            }
            console.error("Starting a silent IdP login failed", error);
            return { status: "unavailable" };
          }
        );
      silentLogin.current = attempt;
      return attempt;
    },
    [getHeldTokens, login, mayStartSilentLogin]
  );
}
